import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { FiMaximize2 } from "react-icons/fi";
import { FaSyncAlt } from "react-icons/fa";
import { FaCheck, FaTriangleExclamation, FaXmark } from "react-icons/fa6";
import MailchimpStatusDot from "../components/mailchimp/MailchimpStatusDot";
import Spinner from "../components/Spinner";
import { LdsOverviewHero, LdsOverviewPage } from "../components/lds/LdsOverview";
import {
  LdsTable,
  LdsTableBody,
  LdsTableCard,
  LdsTableCell,
  LdsTableHead,
  LdsTableHeadCell,
  LdsTableRow,
  LdsTableScroll,
} from "../components/lds/LdsTable";
import http from "../api/apiClient";
import { useCan } from "../authz/useCan";

import BackButton from "../components/navigation/BackButton";
type MailchimpStatusResponse = {
  ok?: boolean;
  upserted?: number;
  message?: string;
  lastSyncedAt?: string;
  lastRunId?: string | number | null;
  runId?: string | number | null;
  continuousSyncEnabled?: boolean;
  nextScheduledAt?: string | number | null;
  scheduledEnabled?: boolean;
  scheduledCron?: string | null;
  scheduledZone?: string | null;
  trigger?: string;
  reason?: string;
  initiatedBy?: string;
};

type MailchimpSettingsResponse = {
  newsletterTag?: string | null;
  alumniTag?: string | null;
  teacherTag?: string | null;
};

type MaintenanceRecipientDTO = {
  type?: "ouder" | "docent" | string | null;
  email?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  name?: string | null;
  newsletterStatus?: string | null;
  newsletterOptOut?: boolean | null;
  optedOut?: boolean | null;
};

type MailchimpSyncRunEmailDetail = Record<string, any>;

type MailchimpSyncRunDetailResponse = {
  id?: string | number | null;
  suppressedEmails?: Record<string, MailchimpSyncRunEmailDetail> | null;
  failedEmails?: Record<string, MailchimpSyncRunEmailDetail> | null;
  invalidEmails?: Record<string, MailchimpSyncRunEmailDetail> | null;
};

const parseDateTime = (value?: string) => {
  if (!value) return null;
  const raw = String(value).trim();
  if (!raw) return null;

  if (/^\d+(?:\.\d+)?$/.test(raw)) {
    const numeric = Number(raw);
    if (!Number.isFinite(numeric)) return null;
    const ms = numeric > 1e12 ? numeric : numeric * 1000;
    const date = new Date(ms);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  let normalized = raw;
  if (normalized.includes(" ") && !normalized.includes("T")) {
    normalized = normalized.replace(" ", "T");
  }
  if (/^\d{4}-\d{2}-\d{2}$/.test(normalized)) {
    normalized = `${normalized}T00:00:00`;
  }

  const date = new Date(normalized);
  return Number.isNaN(date.getTime()) ? null : date;
};

const formatDateTime = (value?: string) => {
  const date = parseDateTime(value);
  if (!date) return "—";
  return date.toLocaleString("nl-NL", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const parseFilename = (contentDisposition?: string | null) => {
  if (!contentDisposition) return null;
  const match = /filename\*?=(?:UTF-8''|"?)([^";]+)"?/i.exec(contentDisposition);
  if (!match?.[1]) return null;
  try {
    return decodeURIComponent(match[1]);
  } catch {
    return match[1];
  }
};

const extractTimestampFromText = (text?: string) => {
  if (!text) return null;
  const raw = String(text);
  const match =
    raw.match(/\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?Z/) ||
    raw.match(/\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}/) ||
    raw.match(/\b\d{10,13}(?:\.\d+)?\b/) ||
    raw.match(/\d{4}-\d{2}-\d{2}/);
  return match?.[0] ?? null;
};

const stripSuppressedMarker = (text?: string | null) => {
  const raw = String(text ?? "");
  if (!raw) return { text: "", suppressed: false };
  let suppressed = false;
  const cleaned = raw.replace(/\(?\s*suppressed\s*=\s*1\s*\)?/gi, (m) => {
    suppressed = true;
    return "";
  });
  return { text: cleaned.replace(/\s{2,}/g, " ").trim(), suppressed };
};

const isEmailLikelyValid = (email?: string | null) => {
  const e = String(email || "").trim();
  if (!e) return false;
  // Basic sanity check (not RFC-complete, but good enough for UI indicator)
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);
};

const normalizeRecipient = (src: MaintenanceRecipientDTO) => {
  const typeRaw = String(src?.type || "").trim().toLowerCase();
  const type = typeRaw === "docent" ? "docent" : typeRaw === "ouder" ? "ouder" : typeRaw || "onbekend";

  const firstName = src?.firstName != null ? String(src.firstName).trim() : "";
  const lastName = src?.lastName != null ? String(src.lastName).trim() : "";
  const composedName = [firstName, lastName].filter(Boolean).join(" ").trim();
  const name = (src?.name != null ? String(src.name).trim() : "") || composedName || "—";

  const email = src?.email != null ? String(src.email).trim() : "";
  const newsletterStatus = src?.newsletterStatus != null ? String(src.newsletterStatus).trim() : "";
  const optedOut = Boolean(src?.newsletterOptOut ?? (src as any)?.optedOut);
  const emailStatusRaw =
    // flat
    (src as any)?.emailStatus ??
    (src as any)?.email_status ??
    // nested
    (src as any)?.emailValidation?.status ??
    (src as any)?.emailValidation?.kind ??
    (src as any)?.emailAssessment?.status ??
    (src as any)?.emailAssessment?.kind ??
    null;
  const emailStatus = emailStatusRaw != null ? String(emailStatusRaw).trim().toLowerCase() : "";
  const normalizedEmailStatus =
    emailStatus === "valid" || emailStatus === "ok"
      ? "valid"
      : emailStatus === "invalid" || emailStatus === "error"
        ? "invalid"
        : emailStatus === "suspicious" || emailStatus === "warning" || emailStatus === "warn"
          ? "suspicious"
          : "";

  return { type, name, email, newsletterStatus, optedOut, emailStatus: normalizedEmailStatus };
};

const AdminMaintenanceMailchimp = () => {
  const { t } = useTranslation();
  const pollTimerRef = useRef<number | null>(null);

  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [alumniSyncing, setAlumniSyncing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [syncFeedback, setSyncFeedback] = useState<string | null>(null);
  const [status, setStatus] = useState<MailchimpStatusResponse | null>(null);
  const [hasNeverSynced, setHasNeverSynced] = useState(false);
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [settingsError, setSettingsError] = useState<string | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settingsConfirm, setSettingsConfirm] = useState("");
  const [settings, setSettings] = useState<Required<MailchimpSettingsResponse>>({
    newsletterTag: "",
    alumniTag: "",
    teacherTag: "",
  });

  const [maintenanceOpen, setMaintenanceOpen] = useState(false);
  const [maintenanceLoading, setMaintenanceLoading] = useState(false);
  const [maintenanceError, setMaintenanceError] = useState<string | null>(null);
  const [maintenanceIncludeParents, setMaintenanceIncludeParents] = useState(true);
  const [maintenanceIncludeTeachers, setMaintenanceIncludeTeachers] = useState(true);
  const [maintenanceFilterOpen, setMaintenanceFilterOpen] = useState(false);
  const [maintenanceEmailStatusFilters, setMaintenanceEmailStatusFilters] = useState<string[]>([]); // valid | invalid | suspicious | unknown
  const [maintenanceRecipients, setMaintenanceRecipients] = useState<
    Array<ReturnType<typeof normalizeRecipient>>
  >([]);

  const canStatus = useCan("newsletter.recipients", "status");
  const canExport = useCan("newsletter.recipients", "export");
  const canSync = useCan("newsletter.recipients", "sync");
  const canViewRuns = useCan("newsletter.recipients", "runs");
  const canRunSync = canExport && canSync;
  const canViewPage = canStatus || canRunSync;
  const alumniSyncFeatureEnabled = false;
  const canRunAlumniSync = alumniSyncFeatureEnabled && canSync;

  const nextScheduledLabel = useMemo(() => {
    const value = status?.nextScheduledAt;
    if (value == null || value === "") return null;
    const formatted = formatDateTime(String(value));
    return formatted === "—" ? String(value) : formatted;
  }, [status?.nextScheduledAt]);

  const prettyMessage = useMemo(() => {
    if (!status?.message) return null;
    const ts = extractTimestampFromText(status.message);
    if (!ts) return String(status.message);
    const pretty = formatDateTime(ts);
    return String(status.message).replace(ts, pretty === "—" ? ts : pretty);
  }, [status?.message]);

  const triggerText = useMemo(() => {
    const raw = status?.trigger || status?.reason || status?.initiatedBy;
    if (!raw) return null;
    return String(raw).trim() || null;
  }, [status?.initiatedBy, status?.reason, status?.trigger]);

  const shouldShowSoftSpinner = useMemo(() => {
    if (syncing) return true;
    if (!status || hasNeverSynced) return false;
    // In prod we sometimes don't get `ok: true` while the scheduler is enabled;
    // show the "soft" spinner also when scheduling/continuous sync is active.
    const schedulerActive =
      status?.continuousSyncEnabled === true ||
      status?.scheduledEnabled === true ||
      status?.nextScheduledAt != null;
    return status?.ok === true || schedulerActive;
  }, [hasNeverSynced, status, syncing]);

  const continuousSyncEnabled = Boolean(status?.continuousSyncEnabled);
  const scheduledEnabled = Boolean(status?.scheduledEnabled);

  const [messageExpanded, setMessageExpanded] = useState(false);
  const [messageDrawerOpen, setMessageDrawerOpen] = useState(false);
  const [messageRunDetail, setMessageRunDetail] = useState<MailchimpSyncRunDetailResponse | null>(null);
  const [messageRunDetailLoading, setMessageRunDetailLoading] = useState(false);
  const [messageRunDetailError, setMessageRunDetailError] = useState<string | null>(null);
  const messageText = useMemo(() => {
    if (hasNeverSynced) {
      return t("admin.maintenance.mailchimp.never_synced", { defaultValue: "Nog nooit gesynct." });
    }
    if (!status) return null;
    return String(prettyMessage || status?.message || "").trim() || null;
  }, [hasNeverSynced, prettyMessage, status, t]);

  const latestRunId = useMemo(() => {
    const candidate = (status as any)?.lastRunId ?? (status as any)?.runId ?? null;
    if (candidate == null || candidate === "") return null;
    return String(candidate).trim() || null;
  }, [status]);

  useEffect(() => {
    if (!messageDrawerOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setMessageDrawerOpen(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [messageDrawerOpen]);

  useEffect(() => {
    if (!messageDrawerOpen) return;
    setMessageRunDetail(null);
    setMessageRunDetailError(null);
    if (!canViewRuns || !latestRunId) return;

    let cancelled = false;
    const load = async () => {
      setMessageRunDetailLoading(true);
      try {
        const res = await http.get(`/admin/newsletter/recipients/mailchimp/runs/${latestRunId}`);
        if (cancelled) return;
        setMessageRunDetail((res?.data || {}) as MailchimpSyncRunDetailResponse);
      } catch (e: any) {
        if (cancelled) return;
        setMessageRunDetailError(
          e?.response?.data?.message || e?.message || "Failed to load run details."
        );
      } finally {
        if (!cancelled) setMessageRunDetailLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [canViewRuns, latestRunId, messageDrawerOpen]);

  const messageMeta = useMemo(() => {
    const parsed = stripSuppressedMarker(messageText);
    return {
      text: parsed.text || messageText || null,
      suppressed: parsed.suppressed,
    };
  }, [messageText]);

  const messagePreview = useMemo(() => {
    const effective = messageMeta.text;
    if (!effective) return null;
    if ((effective?.length || 0) <= 160) return effective;
    return `${effective.slice(0, 160)}…`;
  }, [messageMeta.text]);

  const statusSummary = useMemo(() => {
    if (hasNeverSynced) {
      return t("admin.maintenance.mailchimp.never_synced", { defaultValue: "Nog nooit gesynct." });
    }
    if (!status) return "—";
    if (prettyMessage) return prettyMessage;
    if (status?.ok === true) return t("common.ok", { defaultValue: "OK" });
    if (status?.ok === false) return t("common.error", { defaultValue: "Fout" });
    return "—";
  }, [hasNeverSynced, prettyMessage, status, t]);

  const loadStatus = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setHasNeverSynced(false);
      const res = await http.get("/admin/newsletter/recipients/mailchimp/status", {
        validateStatus: (code) => (code >= 200 && code < 300) || code === 204,
      });
      if (res.status === 204) {
        setStatus(null);
        setHasNeverSynced(true);
        return;
      }
      const data: MailchimpStatusResponse = res?.data || {};
      setStatus(data);
    } catch (e: any) {
      const message =
        e?.response?.status === 404
          ? t("admin.maintenance.mailchimp.endpoint_missing", {
              defaultValue: "De Mailchimp endpoint is (nog) niet beschikbaar op de backend.",
            })
          : e?.response?.data?.message || e?.message || "Failed to load Mailchimp status.";
      setError(message);
      setStatus(null);
      setHasNeverSynced(false);
    } finally {
      setLoading(false);
    }
  }, [t]);

  const loadSettings = useCallback(async () => {
    setSettingsLoading(true);
    setSettingsError(null);
    try {
      const res = await http.get("/admin/mailchimp/settings");
      const data: MailchimpSettingsResponse = res?.data || {};
      setSettings({
        newsletterTag: String(data?.newsletterTag ?? ""),
        alumniTag: String(data?.alumniTag ?? ""),
        teacherTag: String(data?.teacherTag ?? ""),
      });
    } catch (e: any) {
      const message =
        e?.response?.status === 404
          ? t("admin.maintenance.mailchimp.settings_missing", {
              defaultValue: "De Mailchimp settings endpoint is (nog) niet beschikbaar op de backend.",
            })
          : e?.response?.data?.message || e?.message || "Failed to load Mailchimp settings.";
      setSettingsError(message);
    } finally {
      setSettingsLoading(false);
    }
  }, [t]);

  useEffect(() => {
    if (!canStatus) return;
    loadStatus();
  }, [canStatus, loadStatus]);

  useEffect(() => {
    return () => {
      if (pollTimerRef.current) window.clearInterval(pollTimerRef.current);
    };
  }, []);

  const runSyncNow = async () => {
    if (syncing) return;
    setSyncing(true);
    setError(null);
    setSyncFeedback(null);
    try {
      const res = await http.get("/admin/newsletter/recipients/export", {
        params: { syncMailchimp: "true" },
        responseType: "blob",
      });
      const contentType = String(res?.headers?.["content-type"] || "").toLowerCase();
      const blob = res?.data as Blob;
      if (contentType.includes("application/json")) {
        const text = await blob.text();
        const data = text ? JSON.parse(text) : {};
        const message = data?.message || t("admin.maintenance.mailchimp.sync_started", { defaultValue: "Sync is gestart." });
        // Toon geen popup; zet een zachte status in de pagina via error=null
        setError(null);
        setSyncFeedback(String(message));
      } else {
        const filename =
          parseFilename(res?.headers?.["content-disposition"]) ||
          `recipients-export-${new Date().toISOString().slice(0, 10)}.csv`;
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
        // Geen popup; downloaden is genoeg feedback.
        setError(null);
        setSyncFeedback(
          t("admin.maintenance.mailchimp.download_started", {
            defaultValue: "Download gestart.",
          })
        );
      }
      await loadStatus();

      if (canStatus) {
        const startedAt = Date.now();
        if (pollTimerRef.current) window.clearInterval(pollTimerRef.current);
        pollTimerRef.current = window.setInterval(() => {
          if (Date.now() - startedAt > 30_000) {
            if (pollTimerRef.current) window.clearInterval(pollTimerRef.current);
            pollTimerRef.current = null;
            return;
          }
          loadStatus();
        }, 2500);
      }
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || "Failed to start Mailchimp sync.");
    } finally {
      setSyncing(false);
    }
  };

  const runAlumniSyncNow = async () => {
    if (alumniSyncing) return;
    setAlumniSyncing(true);
    setError(null);
    try {
      await http.post("/admin/mailchimp/alumni/sync");
      await loadStatus();
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || "Failed to start alumni Mailchimp sync.");
    } finally {
      setAlumniSyncing(false);
    }
  };

  const saveSettings = async () => {
    if (settingsSaving) return;
    setSettingsSaving(true);
    setSettingsError(null);
    try {
      await http.put("/admin/mailchimp/settings", {
        newsletterTag: settings.newsletterTag,
        alumniTag: settings.alumniTag,
        teacherTag: settings.teacherTag,
      });
      await loadSettings();
    } catch (e: any) {
      setSettingsError(e?.response?.data?.message || e?.message || "Failed to save Mailchimp settings.");
    } finally {
      setSettingsSaving(false);
    }
  };

  const openSettings = async () => {
    setSettingsOpen(true);
    setSettingsConfirm("");
    await loadSettings();
  };

  const loadMaintenanceRecipients = useCallback(async () => {
    if (!canExport) return;
    setMaintenanceLoading(true);
    setMaintenanceError(null);
    try {
      const res = await http.get("/admin/newsletter/recipients/maintenance", {
        params: {
          includeParents: maintenanceIncludeParents ? "true" : "false",
          includeTeachers: maintenanceIncludeTeachers ? "true" : "false",
        },
      });
      const data = Array.isArray(res?.data) ? res.data : [];
      setMaintenanceRecipients(data.map((r) => normalizeRecipient(r)));
    } catch (e: any) {
      setMaintenanceError(
        e?.response?.data?.message || e?.message || "Failed to load maintenance recipients."
      );
      setMaintenanceRecipients([]);
    } finally {
      setMaintenanceLoading(false);
    }
  }, [canExport, maintenanceIncludeParents, maintenanceIncludeTeachers]);

  useEffect(() => {
    if (!maintenanceOpen) return;
    setMaintenanceFilterOpen(false);
    loadMaintenanceRecipients();
  }, [maintenanceOpen, loadMaintenanceRecipients]);

  const maintenanceEmailStatusKey = useCallback((r: ReturnType<typeof normalizeRecipient>) => {
    const raw = String(r?.emailStatus || "").trim().toLowerCase();
    if (raw === "valid") return "valid";
    if (raw === "invalid") return "invalid";
    if (raw === "suspicious") return "suspicious";
    return "unknown";
  }, []);

  const toggleMaintenanceEmailStatusFilter = useCallback((key: string) => {
    const normalized = String(key || "").trim().toLowerCase();
    if (!normalized) return;
    setMaintenanceEmailStatusFilters((prev) => {
      const next = new Set(Array.isArray(prev) ? prev : []);
      if (next.has(normalized)) next.delete(normalized);
      else next.add(normalized);
      return Array.from(next);
    });
  }, []);

  const filteredMaintenanceRecipients = useMemo(() => {
    const list = Array.isArray(maintenanceRecipients) ? maintenanceRecipients : [];
    const typeFiltered = list.filter((r) => {
      if (r?.type === "ouder" && !maintenanceIncludeParents) return false;
      if (r?.type === "docent" && !maintenanceIncludeTeachers) return false;
      return true;
    });
    if (!Array.isArray(maintenanceEmailStatusFilters) || maintenanceEmailStatusFilters.length === 0) return typeFiltered;
    const selected = new Set(maintenanceEmailStatusFilters.map((v) => String(v).trim().toLowerCase()).filter(Boolean));
    return typeFiltered.filter((r) => selected.has(maintenanceEmailStatusKey(r)));
  }, [
    maintenanceEmailStatusFilters,
    maintenanceEmailStatusKey,
    maintenanceIncludeParents,
    maintenanceIncludeTeachers,
    maintenanceRecipients,
  ]);

  return (
    <LdsOverviewPage>
      <LdsOverviewHero
        icon={FaSyncAlt}
        eyebrow={t("admin.maintenance.eyebrow", { defaultValue: "Maintenance" })}
        title={t("admin.maintenance.mailchimp.title", { defaultValue: "Mailchimp sync" })}
        description={t("admin.maintenance.mailchimp.description", {
          defaultValue: "Schakel de sync in/uit, start een sync en bekijk de laatste status.",
        })}
        actions={
          <>
            <MailchimpStatusDot />
            <BackButton fallback="/maintenance" />
          </>
        }
      />
      <div className="space-y-6 text-gray-900 dark:text-gray-100">
        {(error || !canViewPage) && (
          <div className="rounded-[24px] border border-slate-200 bg-white p-5 shadow-sm dark:border-white/10 dark:bg-slate-900/80">
            {error && (
              <div className="mt-4 rounded-2xl border border-red-200/80 bg-red-50/90 px-4 py-3 text-sm text-red-800 shadow-sm dark:border-red-500/40 dark:bg-red-900/40 dark:text-red-100">
                {error}
              </div>
            )}

            {!canViewPage && (
              <div className="mt-4 rounded-2xl border border-yellow-200/80 bg-yellow-50/90 px-4 py-3 text-sm text-yellow-900 shadow-sm dark:border-yellow-500/40 dark:bg-yellow-900/30 dark:text-yellow-100">
                {t("admin.maintenance.mailchimp.no_access", {
                  defaultValue: "Geen toegang tot Mailchimp sync (permissies ontbreken).",
                })}
              </div>
            )}
          </div>
        )}

        <div className="grid gap-6 md:grid-cols-2">
          <div className="rounded-[24px] border border-slate-200 bg-white p-6 shadow-sm dark:border-white/10 dark:bg-slate-900/80">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <h3 className="text-lg font-semibold text-indigo-900 dark:text-indigo-100">
                {t("admin.maintenance.mailchimp.actions", { defaultValue: "Acties" })}
              </h3>
              {shouldShowSoftSpinner && (
                <span
                  className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-semibold ${
                    syncing
                      ? "border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-500/40 dark:bg-emerald-900/25 dark:text-emerald-100"
                      : "border-emerald-200/70 bg-emerald-50/70 text-emerald-700 dark:border-emerald-500/30 dark:bg-emerald-900/15 dark:text-emerald-200"
                  }`}
                  title={
                    syncing
                      ? t("admin.maintenance.mailchimp.syncing", { defaultValue: "Sync bezig…" })
                      : t("admin.maintenance.mailchimp.ok_hint", { defaultValue: "Laatste status: OK" })
                  }
                >
                  <span
                    className="inline-flex animate-spin"
                    style={{ animationDuration: syncing ? "0.9s" : "2.8s" }}
                  >
                    <FaSyncAlt />
                  </span>
                  {syncing
                    ? t("admin.maintenance.mailchimp.syncing", { defaultValue: "Sync bezig…" })
                    : t("admin.maintenance.mailchimp.soft_ok", { defaultValue: "OK" })}
                </span>
              )}
            </div>

            {continuousSyncEnabled && (
              <div className="mt-3">
                <span className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-800 shadow-sm dark:border-emerald-500/40 dark:bg-emerald-900/25 dark:text-emerald-100">
                  <span className="inline-flex animate-spin" style={{ animationDuration: "2.6s" }}>
                    <FaSyncAlt />
                  </span>
                  {t("admin.maintenance.mailchimp.continuous_on", { defaultValue: "Continuous sync: aan" })}
                </span>
              </div>
            )}

            <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
              {t("admin.maintenance.mailchimp.actions_help", {
                defaultValue: "Start een sync naar Mailchimp.",
              })}
            </p>

            {syncFeedback && (
              <div className="mt-3 text-sm font-semibold text-emerald-700 dark:text-emerald-200">
                {syncFeedback}
              </div>
            )}

            <div className="mt-4 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={runSyncNow}
                disabled={!canRunSync || syncing || loading}
                title={
                  canRunSync
                    ? t("admin.maintenance.mailchimp.sync_hint", { defaultValue: "Start een sync naar Mailchimp." })
                    : t("admin.maintenance.mailchimp.missing_perms_sync", {
                        defaultValue: "Je mist permissies: newsletter.recipients:export en/of newsletter.recipients:sync.",
                      })
                }
                className={`inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-semibold shadow-sm transition ${
                  canRunSync
                    ? "bg-indigo-600 text-white hover:-translate-y-0.5 hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-60"
                    : "bg-slate-200 text-slate-600 dark:bg-slate-800 dark:text-slate-300 cursor-not-allowed opacity-70"
                }`}
              >
                <FaSyncAlt />
                {syncing
                  ? t("common.loading", { defaultValue: "Bezig..." })
                  : t("admin.maintenance.mailchimp.sync_now", { defaultValue: "Sync nu" })}
              </button>

              {canRunAlumniSync && (
                <button
                  type="button"
                  onClick={runAlumniSyncNow}
                  disabled={alumniSyncing || syncing || loading}
                  className="inline-flex items-center gap-2 rounded-xl border border-indigo-200/70 bg-white/80 px-4 py-2 text-sm font-semibold text-indigo-700 shadow-sm transition hover:-translate-y-0.5 hover:bg-white hover:text-indigo-900 disabled:cursor-not-allowed disabled:opacity-60 dark:border-indigo-500/40 dark:bg-gray-900/50 dark:text-indigo-200 dark:hover:bg-gray-900/70"
                  title={t("admin.maintenance.mailchimp.alumni_sync_hint", {
                    defaultValue: "Start een alumni sync naar Mailchimp (admin endpoint).",
                  })}
                >
                  <FaSyncAlt />
                  {alumniSyncing
                    ? t("common.loading", { defaultValue: "Bezig..." })
                    : t("admin.maintenance.mailchimp.alumni_sync", { defaultValue: "Sync alumni" })}
                </button>
              )}
            </div>

            {!canRunSync && (
              <div className="mt-4 rounded-2xl border border-yellow-200/80 bg-yellow-50/90 px-4 py-3 text-sm text-yellow-900 shadow-sm dark:border-yellow-500/40 dark:bg-yellow-900/30 dark:text-yellow-100">
                {t("admin.maintenance.mailchimp.missing_perms_sync", {
                  defaultValue: "Je mist permissies: newsletter.recipients:export en/of newsletter.recipients:sync.",
                })}
              </div>
            )}

          </div>

          <div className="rounded-[24px] border border-slate-200 bg-white p-6 shadow-sm dark:border-white/10 dark:bg-slate-900/80">
            <h3 className="text-lg font-semibold text-indigo-900 dark:text-indigo-100">
              {t("admin.maintenance.mailchimp.status", { defaultValue: "Status" })}
            </h3>

            {!canStatus && (
              <div className="mt-4 rounded-2xl border border-yellow-200/80 bg-yellow-50/90 px-4 py-3 text-sm text-yellow-900 shadow-sm dark:border-yellow-500/40 dark:bg-yellow-900/30 dark:text-yellow-100">
                {t("admin.maintenance.mailchimp.missing_perms_status", {
                  defaultValue: "Je mist permissie: newsletter.recipients:status.",
                })}
              </div>
            )}

            {canStatus && (
              <div className="mt-4 grid gap-3 sm:grid-cols-2">
              <div className="rounded-2xl border border-indigo-50/70 bg-indigo-50/40 px-3 py-2 text-sm dark:border-indigo-500/30 dark:bg-indigo-900/20">
                <p className="text-xs uppercase tracking-wide text-indigo-700 dark:text-indigo-200">
                  {t("admin.maintenance.mailchimp.fields.ok", { defaultValue: "OK" })}
                </p>
                <p className="mt-1 font-semibold text-gray-900 dark:text-white">
                  {hasNeverSynced ? "—" : status?.ok === true ? "Ja" : status?.ok === false ? "Nee" : "—"}
                </p>
              </div>
              <div className="rounded-2xl border border-indigo-50/70 bg-indigo-50/40 px-3 py-2 text-sm dark:border-indigo-500/30 dark:bg-indigo-900/20">
                <p className="text-xs uppercase tracking-wide text-indigo-700 dark:text-indigo-200">
                  {t("admin.maintenance.mailchimp.fields.upserted", { defaultValue: "Upserted" })}
                </p>
                <p className="mt-1 font-semibold text-gray-900 dark:text-white">
                  {typeof status?.upserted === "number" ? String(status.upserted) : "—"}
                </p>
              </div>
              <div className="rounded-2xl border border-indigo-50/70 bg-indigo-50/40 px-3 py-2 text-sm dark:border-indigo-500/30 dark:bg-indigo-900/20">
                <p className="text-xs uppercase tracking-wide text-indigo-700 dark:text-indigo-200">
                  {t("admin.maintenance.mailchimp.fields.message", { defaultValue: "Bericht" })}
                </p>
                <div className="mt-1 space-y-2">
                  <div className="whitespace-pre-wrap break-words font-semibold text-gray-900 dark:text-white">
                    {messageExpanded ? (messageMeta.text || statusSummary) : (messagePreview || statusSummary)}
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    {((messageMeta.text?.length || 0) > 160) && (
                      <button
                        type="button"
                        onClick={() => setMessageExpanded((v) => !v)}
                        className="inline-flex items-center rounded-full border border-indigo-200 bg-white px-3 py-1 text-xs font-semibold text-indigo-700 shadow-sm hover:bg-indigo-50 dark:border-indigo-500/40 dark:bg-gray-900/40 dark:text-indigo-200 dark:hover:bg-gray-900/60"
                      >
                        {messageExpanded
                          ? t("common.less", { defaultValue: "Minder" })
                          : t("common.more", { defaultValue: "Meer" })}
                      </button>
                    )}
                    {messageMeta.text && canViewRuns && (
                      <button
                        type="button"
                        onClick={() => setMessageDrawerOpen(true)}
                        title={t("admin.maintenance.mailchimp.message_full", { defaultValue: "Volledig bericht" })}
                        className="inline-flex items-center gap-2 rounded-full border border-slate-200/70 bg-white/70 px-3 py-1 text-xs font-semibold text-slate-700 shadow-sm transition hover:bg-white hover:text-slate-900 dark:border-slate-700/60 dark:bg-slate-900/20 dark:text-slate-200"
                      >
                        <FiMaximize2 />
                        {t("admin.maintenance.mailchimp.message_full", { defaultValue: "Volledig" })}
                      </button>
                    )}
                    {messageMeta.suppressed && (
                      <span className="inline-flex items-center rounded-full border border-slate-200/70 bg-slate-50/70 px-2 py-0.5 text-[11px] font-semibold text-slate-700 dark:border-slate-700/60 dark:bg-slate-900/20 dark:text-slate-200">
                        suppressed=1
                      </span>
                    )}
                  </div>
                </div>
                {triggerText && (
                  <p className="mt-1 text-xs text-gray-600 dark:text-gray-300">
                    {t("admin.maintenance.mailchimp.fields.trigger", { defaultValue: "Trigger" })}: {triggerText}
                  </p>
                )}
              </div>
              {nextScheduledLabel && (
                <div className="rounded-2xl border border-indigo-50/70 bg-indigo-50/40 px-3 py-2 text-sm dark:border-indigo-500/30 dark:bg-indigo-900/20">
                  <p className="text-xs uppercase tracking-wide text-indigo-700 dark:text-indigo-200">
                    {t("admin.maintenance.mailchimp.fields.nextScheduledAt", { defaultValue: "Volgende sync" })}
                  </p>
                  <div className="mt-1 flex items-center gap-2 font-semibold text-gray-900 dark:text-white">
                    {(scheduledEnabled || continuousSyncEnabled) && (
                      <span
                        className="inline-flex animate-spin text-emerald-600 dark:text-emerald-300"
                        style={{ animationDuration: "2.6s" }}
                        title={t("admin.maintenance.mailchimp.scheduler_on", { defaultValue: "Scheduler actief" })}
                        aria-label={t("admin.maintenance.mailchimp.scheduler_on", { defaultValue: "Scheduler actief" })}
                      >
                        <FaSyncAlt />
                      </span>
                    )}
                    <span>{nextScheduledLabel}</span>
                  </div>
                  {(status?.scheduledCron || status?.scheduledZone) && (
                    <p className="mt-1 text-xs text-gray-600 dark:text-gray-300 break-words">
                      {[
                        status?.scheduledCron ? `cron: ${status.scheduledCron}` : null,
                        status?.scheduledZone ? `zone: ${status.scheduledZone}` : null,
                      ]
                        .filter(Boolean)
                        .join(" • ")}
                    </p>
                  )}
                </div>
              )}
              </div>
            )}
          </div>

          <div className="rounded-[24px] border border-slate-200 bg-white p-6 shadow-sm dark:border-white/10 dark:bg-slate-900/80">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <h3 className="text-lg font-semibold text-indigo-900 dark:text-indigo-100">
                {t("admin.maintenance.mailchimp.settings", { defaultValue: "Tags" })}
              </h3>
              {settingsOpen ? (
                <button
                  type="button"
                  onClick={() => {
                    setSettingsOpen(false);
                    setSettingsConfirm("");
                    setSettingsError(null);
                  }}
                  disabled={settingsLoading || settingsSaving}
                  className="inline-flex items-center gap-2 rounded-xl border border-indigo-100/70 bg-white/80 px-3 py-2 text-sm font-semibold text-indigo-700 shadow-sm transition hover:-translate-y-0.5 hover:bg-white disabled:cursor-not-allowed disabled:opacity-60 dark:border-indigo-500/40 dark:bg-gray-900/60 dark:text-indigo-200"
                >
                  {t("common.close", { defaultValue: "Sluiten" })}
                </button>
              ) : (
                <button
                  type="button"
                  onClick={openSettings}
                  disabled={settingsLoading || settingsSaving}
                  className="inline-flex items-center gap-2 rounded-xl border border-indigo-100/70 bg-white/80 px-3 py-2 text-sm font-semibold text-indigo-700 shadow-sm transition hover:-translate-y-0.5 hover:bg-white disabled:cursor-not-allowed disabled:opacity-60 dark:border-indigo-500/40 dark:bg-gray-900/60 dark:text-indigo-200"
                >
                  {t("common.edit", { defaultValue: "Bewerken" })}
                </button>
              )}
            </div>

            <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
              {t("admin.maintenance.mailchimp.settings_help", {
                defaultValue: "Pas de Mailchimp tags aan die gebruikt worden voor synchronisatie.",
              })}
            </p>

            {!settingsOpen && (
              <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">
                {t("admin.maintenance.mailchimp.settings_hidden_hint", {
                  defaultValue: "Tags aanpassen is een geavanceerde actie en staat daarom verborgen achter 'Bewerken'.",
                })}
              </p>
            )}

            {settingsError && (
              <div className="mt-4 rounded-2xl border border-red-200/80 bg-red-50/90 px-4 py-3 text-sm text-red-800 shadow-sm dark:border-red-500/40 dark:bg-red-900/40 dark:text-red-100">
                {settingsError}
              </div>
            )}

            {settingsOpen && (
              <div className="mt-4 grid gap-3">
                <div className="rounded-2xl border border-amber-200/80 bg-amber-50/80 px-4 py-3 text-sm text-amber-900 shadow-sm dark:border-amber-500/40 dark:bg-amber-900/25 dark:text-amber-100">
                  {t("admin.maintenance.mailchimp.settings_confirm_hint", {
                    defaultValue: "Typ TAGS om aanpassingen te kunnen opslaan.",
                  })}
                  <div className="mt-2">
                    <input
                      value={settingsConfirm}
                      onChange={(e) => setSettingsConfirm(e.target.value)}
                      className="lumen-glow-input w-full px-4 py-2 text-gray-900 dark:text-white"
                      placeholder="TAGS"
                      disabled={settingsLoading || settingsSaving}
                    />
                  </div>
                </div>
                <label className="flex flex-col gap-1">
                  <span className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                    {t("admin.maintenance.mailchimp.newsletter_tag", { defaultValue: "Newsletter tag" })}
                  </span>
                  <input
                    value={settings.newsletterTag}
                    onChange={(e) => setSettings((s) => ({ ...s, newsletterTag: e.target.value }))}
                    className="lumen-glow-input w-full px-4 py-2 text-gray-900 dark:text-white"
                    placeholder={t("admin.maintenance.mailchimp.newsletter_tag_placeholder", {
                      defaultValue: "nieuwsbrief",
                    })}
                    disabled={settingsLoading || settingsSaving}
                  />
                </label>

                <label className="flex flex-col gap-1">
                  <span className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                    {t("admin.maintenance.mailchimp.alumni_tag", { defaultValue: "Alumni tag" })}
                  </span>
                  <input
                    value={settings.alumniTag}
                    onChange={(e) => setSettings((s) => ({ ...s, alumniTag: e.target.value }))}
                    className="lumen-glow-input w-full px-4 py-2 text-gray-900 dark:text-white"
                    placeholder={t("admin.maintenance.mailchimp.alumni_tag_placeholder", { defaultValue: "alumni" })}
                    disabled={settingsLoading || settingsSaving}
                  />
                </label>

                <label className="flex flex-col gap-1">
                  <span className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                    {t("admin.maintenance.mailchimp.teacher_tag", { defaultValue: "Teacher tag" })}
                  </span>
                  <input
                    value={settings.teacherTag}
                    onChange={(e) => setSettings((s) => ({ ...s, teacherTag: e.target.value }))}
                    className="lumen-glow-input w-full px-4 py-2 text-gray-900 dark:text-white"
                    placeholder={t("admin.maintenance.mailchimp.teacher_tag_placeholder", { defaultValue: "Docent" })}
                    disabled={settingsLoading || settingsSaving}
                  />
                </label>

                <div className="mt-2 flex justify-end">
                  <button
                    type="button"
                    onClick={saveSettings}
                    disabled={settingsLoading || settingsSaving || settingsConfirm.trim().toUpperCase() !== "TAGS"}
                    className="inline-flex items-center gap-2 rounded-xl bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:-translate-y-0.5 hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    <FaSyncAlt />
                    {settingsSaving
                      ? t("common.loading", { defaultValue: "Bezig..." })
                      : t("common.save", { defaultValue: "Opslaan" })}
                  </button>
                </div>
              </div>
            )}
          </div>

          {canExport && (
            <div className="rounded-[24px] border border-slate-200 bg-white p-6 shadow-sm dark:border-white/10 dark:bg-slate-900/80">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <h3 className="text-lg font-semibold text-indigo-900 dark:text-indigo-100">
                  {t("admin.maintenance.mailchimp.maintenance_recipients", { defaultValue: "Maintenance recipients" })}
                </h3>
                {maintenanceOpen ? (
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={loadMaintenanceRecipients}
                      disabled={maintenanceLoading}
                      className="inline-flex items-center gap-2 rounded-xl border border-indigo-100/70 bg-white/80 px-3 py-2 text-sm font-semibold text-indigo-700 shadow-sm transition hover:-translate-y-0.5 hover:bg-white disabled:cursor-not-allowed disabled:opacity-60 dark:border-indigo-500/40 dark:bg-gray-900/60 dark:text-indigo-200"
                    >
                      {t("common.refresh", { defaultValue: "Refresh" })}
                    </button>
                    <button
                      type="button"
                      onClick={() => setMaintenanceOpen(false)}
                      disabled={maintenanceLoading}
                      className="inline-flex items-center gap-2 rounded-xl border border-indigo-100/70 bg-white/80 px-3 py-2 text-sm font-semibold text-indigo-700 shadow-sm transition hover:-translate-y-0.5 hover:bg-white disabled:cursor-not-allowed disabled:opacity-60 dark:border-indigo-500/40 dark:bg-gray-900/60 dark:text-indigo-200"
                    >
                      {t("common.close", { defaultValue: "Sluiten" })}
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => setMaintenanceOpen(true)}
                    disabled={maintenanceLoading}
                    className="inline-flex items-center gap-2 rounded-xl border border-indigo-100/70 bg-white/80 px-3 py-2 text-sm font-semibold text-indigo-700 shadow-sm transition hover:-translate-y-0.5 hover:bg-white disabled:cursor-not-allowed disabled:opacity-60 dark:border-indigo-500/40 dark:bg-gray-900/60 dark:text-indigo-200"
                  >
                    {t("common.view", { defaultValue: "View" })}
                  </button>
                )}
              </div>

              <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
                {t("admin.maintenance.mailchimp.maintenance_recipients_help", {
                  defaultValue: "Gecombineerde lijst van ouders en docenten via /admin/newsletter/recipients/maintenance.",
                })}
              </p>

              {maintenanceOpen && (
                <>
                  <div className="mt-4 flex flex-wrap items-center gap-3 text-sm text-gray-700 dark:text-gray-200">
                    <div className="relative ml-auto">
                      <button
                        type="button"
                        onClick={() => setMaintenanceFilterOpen((v) => !v)}
                        className="inline-flex items-center gap-2 rounded-xl border border-indigo-100/70 bg-white/80 px-3 py-2 text-sm font-semibold text-indigo-700 shadow-sm transition hover:-translate-y-0.5 hover:bg-white disabled:cursor-not-allowed disabled:opacity-60 dark:border-indigo-500/40 dark:bg-gray-900/60 dark:text-indigo-200"
                        aria-expanded={maintenanceFilterOpen}
                      >
                        {t("common.filter", { defaultValue: "Filter" })}
                        {(() => {
                          const count =
                            (maintenanceIncludeParents ? 0 : 1) +
                            (maintenanceIncludeTeachers ? 0 : 1) +
                            (maintenanceEmailStatusFilters?.length ? 1 : 0);
                          return count > 0 ? (
                            <span className="rounded-full bg-indigo-600/10 px-2 py-0.5 text-xs font-bold text-indigo-800 dark:bg-indigo-500/20 dark:text-indigo-200">
                              {count}
                            </span>
                          ) : null;
                        })()}
                      </button>

                      {maintenanceFilterOpen && (
                        <div className="absolute right-0 z-50 mt-2 w-[280px] rounded-2xl border border-indigo-100/60 bg-white/95 p-3 shadow-xl backdrop-blur dark:border-indigo-500/40 dark:bg-gray-900/95">
                          <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                            {t("common.type", { defaultValue: "Type" })}
                          </div>
                          <div className="mt-2 flex flex-col gap-2 text-sm text-gray-700 dark:text-gray-200">
                            <label className="inline-flex items-center gap-2">
                              <input
                                type="checkbox"
                                checked={maintenanceIncludeParents}
                                onChange={(e) => setMaintenanceIncludeParents(e.target.checked)}
                              />
                              {t("common.parents", { defaultValue: "Ouders" })}
                            </label>
                            <label className="inline-flex items-center gap-2">
                              <input
                                type="checkbox"
                                checked={maintenanceIncludeTeachers}
                                onChange={(e) => setMaintenanceIncludeTeachers(e.target.checked)}
                              />
                              {t("common.teachers", { defaultValue: "Docenten" })}
                            </label>
                          </div>

                          <div className="mt-4 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                            {t("admin.email_validation.checks.columns.status", { defaultValue: "E-mail status" })}
                          </div>
                          <div className="mt-2 flex flex-col gap-2 text-sm text-gray-700 dark:text-gray-200">
                            {[
                              { id: "valid", label: t("admin.email_validation.status.valid", { defaultValue: "Valide" }) },
                              { id: "invalid", label: t("admin.email_validation.status.invalid", { defaultValue: "Ongeldig" }) },
                              { id: "suspicious", label: t("admin.email_validation.status.suspicious", { defaultValue: "Twijfelachtig" }) },
                              { id: "unknown", label: t("common.unknown", { defaultValue: "Onbekend" }) },
                            ].map((opt) => (
                              <label key={opt.id} className="inline-flex items-center gap-2">
                                <input
                                  type="checkbox"
                                  checked={maintenanceEmailStatusFilters.includes(opt.id)}
                                  onChange={() => toggleMaintenanceEmailStatusFilter(opt.id)}
                                />
                                {opt.label}
                              </label>
                            ))}
                          </div>

                          <div className="mt-4 flex justify-end gap-2">
                            <button
                              type="button"
                              onClick={() => setMaintenanceEmailStatusFilters([])}
                              className="rounded-full border border-slate-200/70 bg-white/70 px-3 py-1 text-xs font-semibold text-slate-700 transition hover:bg-white dark:border-slate-700/60 dark:bg-slate-900/20 dark:text-slate-200"
                            >
                              {t("common.clear", { defaultValue: "Wissen" })}
                            </button>
                            <button
                              type="button"
                              onClick={() => setMaintenanceFilterOpen(false)}
                              className="rounded-full border border-indigo-100/60 bg-indigo-50/70 px-3 py-1 text-xs font-semibold text-indigo-700 transition hover:text-indigo-900 dark:border-indigo-500/40 dark:bg-indigo-900/30 dark:text-indigo-200"
                            >
                              {t("common.close", { defaultValue: "Sluiten" })}
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {maintenanceError && (
                    <div className="mt-4 rounded-2xl border border-red-200/80 bg-red-50/90 px-4 py-3 text-sm text-red-800 shadow-sm dark:border-red-500/40 dark:bg-red-900/40 dark:text-red-100">
                      {maintenanceError}
                    </div>
                  )}

                  <LdsTableCard className="mt-4">
                    <LdsTableScroll>
                      <LdsTable>
                        <LdsTableHead>
                          <LdsTableRow>
                            <LdsTableHeadCell>{t("common.name", { defaultValue: "Name" })}</LdsTableHeadCell>
                            <LdsTableHeadCell>{t("family.common.email", { defaultValue: "Email" })}</LdsTableHeadCell>
                            <LdsTableHeadCell>{t("common.status", { defaultValue: "Status" })}</LdsTableHeadCell>
                          </LdsTableRow>
                        </LdsTableHead>
                        <LdsTableBody>
                          {maintenanceLoading ? (
                            <LdsTableRow>
                              <LdsTableCell colSpan={3}>
                                <div className="flex items-center gap-3 text-sm text-slate-600 dark:text-slate-200">
                                  <Spinner />
                                  {t("common.loading", { defaultValue: "Loading…" })}
                                </div>
                              </LdsTableCell>
                            </LdsTableRow>
                          ) : filteredMaintenanceRecipients.length === 0 ? (
                            <LdsTableRow>
                              <LdsTableCell className="text-gray-600 dark:text-gray-300" colSpan={3}>
                                {t("common.empty", { defaultValue: "No items." })}
                              </LdsTableCell>
                            </LdsTableRow>
                          ) : (
                            filteredMaintenanceRecipients.map((r, idx) => (
                              <LdsTableRow key={`${r.type}-${r.email || idx}`}>
                                <LdsTableCell className="text-gray-900 dark:text-gray-100">
                                  <span className="inline-flex items-center gap-2">
                                    <span className="font-semibold">{r.name}</span>
                                    {r.type === "docent" ? (
                                      <span className="inline-flex items-center rounded-full border border-indigo-200/70 bg-indigo-50/70 px-2 py-0.5 text-[11px] font-semibold text-indigo-800 dark:border-indigo-500/30 dark:bg-indigo-950/20 dark:text-indigo-200">
                                        {t("common.teacher", { defaultValue: "Docent" })}
                                      </span>
                                    ) : r.type === "ouder" ? (
                                      <span className="inline-flex items-center rounded-full border border-emerald-200/70 bg-emerald-50/70 px-2 py-0.5 text-[11px] font-semibold text-emerald-800 dark:border-emerald-500/30 dark:bg-emerald-950/20 dark:text-emerald-200">
                                        {t("common.parent", { defaultValue: "Ouder" })}
                                      </span>
                                    ) : null}
                                  </span>
                                </LdsTableCell>
                                <LdsTableCell className="text-gray-900 dark:text-gray-100">
                                  <span className="inline-flex items-center gap-2">
                                    <span className="max-w-[36ch] truncate" title={r.email || undefined}>
                                      {r.email || "—"}
                                    </span>
                                    {r.emailStatus === "valid" ? (
                                      <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-emerald-500/15 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-200">
                                        <FaCheck size={12} />
                                      </span>
                                    ) : r.emailStatus === "invalid" || (r.emailStatus === "" && r.email && !isEmailLikelyValid(r.email)) ? (
                                      <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-rose-500/15 text-rose-700 dark:bg-rose-500/20 dark:text-rose-200">
                                        <FaXmark size={12} />
                                      </span>
                                    ) : r.emailStatus === "suspicious" ? (
                                      <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-amber-500/15 text-amber-800 dark:bg-amber-500/20 dark:text-amber-200">
                                        <FaTriangleExclamation size={12} />
                                      </span>
                                    ) : null}
                                  </span>
                                </LdsTableCell>
                                <LdsTableCell className="text-gray-700 dark:text-gray-200">
                                  {(() => {
                                    const raw = String(r.newsletterStatus || "").trim();
                                    const normalized = raw.toUpperCase();
                                    if (r.optedOut) {
                                      return (
                                        <span className="inline-flex items-center rounded-full border border-amber-200/70 bg-amber-50/70 px-2 py-0.5 text-[11px] font-semibold text-amber-800 dark:border-amber-500/30 dark:bg-amber-950/20 dark:text-amber-200">
                                          OPTED_OUT
                                        </span>
                                      );
                                    }
                                    if (normalized === "SUBSCRIBED") {
                                      return (
                                        <span className="inline-flex items-center rounded-full border border-emerald-200/70 bg-emerald-50/50 px-2 py-0.5 text-[11px] font-semibold text-emerald-800 dark:border-emerald-500/30 dark:bg-emerald-950/15 dark:text-emerald-200">
                                          Subscribed
                                        </span>
                                      );
                                    }
                                    if (normalized === "UNSUBSCRIBED") {
                                      return (
                                        <span className="inline-flex items-center rounded-full border border-slate-200/70 bg-slate-50/70 px-2 py-0.5 text-[11px] font-semibold text-slate-700 dark:border-slate-700/60 dark:bg-slate-900/20 dark:text-slate-200">
                                          UNSUBSCRIBED
                                        </span>
                                      );
                                    }
                                    if (normalized === "INELIGIBLE") {
                                      return (
                                        <span className="inline-flex items-center rounded-full border border-slate-200/70 bg-slate-50/70 px-2 py-0.5 text-[11px] font-semibold text-slate-700 dark:border-slate-700/60 dark:bg-slate-900/20 dark:text-slate-200">
                                          INELIGIBLE
                                        </span>
                                      );
                                    }
                                    return raw || "—";
                                  })()}
                                </LdsTableCell>
                              </LdsTableRow>
                            ))
                          )}
                        </LdsTableBody>
                      </LdsTable>
                    </LdsTableScroll>
                  </LdsTableCard>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {messageDrawerOpen && (
        <div className="fixed inset-0 z-[9999]">
          <button
            type="button"
            className="absolute inset-0 bg-slate-950/40 backdrop-blur-[1px]"
            onClick={() => setMessageDrawerOpen(false)}
            aria-label={t("common.close", { defaultValue: "Sluiten" })}
          />
          <aside className="absolute right-0 top-0 h-full w-full max-w-[560px] overflow-auto bg-white shadow-2xl dark:bg-gray-950">
            <div className="sticky top-0 z-10 border-b border-slate-200/70 bg-white/95 p-4 backdrop-blur dark:border-slate-800/70 dark:bg-gray-950/90">
              <div className="flex items-center justify-between gap-3">
                <div className="text-sm font-extrabold text-slate-900 dark:text-slate-100">
                  {t("admin.maintenance.mailchimp.fields.message", { defaultValue: "Bericht" })}
                </div>
                <button
                  type="button"
                  onClick={() => setMessageDrawerOpen(false)}
                  className="rounded-full border border-slate-200/70 bg-white px-3 py-1 text-sm font-semibold text-slate-700 transition hover:text-slate-900 dark:border-slate-700/60 dark:bg-slate-900/20 dark:text-slate-200"
                >
                  {t("common.close", { defaultValue: "Sluiten" })}
                </button>
              </div>
            </div>
            <div className="p-4 space-y-3">
              <div className="rounded-2xl border border-slate-200/70 bg-white/70 p-3 dark:border-slate-800/70 dark:bg-slate-950/10">
                <div className="whitespace-pre-wrap break-words text-sm font-semibold text-slate-900 dark:text-slate-100">
                  {messageMeta.text || statusSummary || "—"}
                </div>
              </div>

              {(messageRunDetailLoading || messageRunDetailError || messageRunDetail) && (
                <div className="rounded-2xl border border-slate-200/70 bg-white/70 p-3 dark:border-slate-800/70 dark:bg-slate-950/10">
                  <div className="flex items-center justify-between gap-3">
                    <div className="text-xs font-semibold uppercase tracking-wide text-slate-600 dark:text-slate-300">
                      {t("admin.maintenance.mailchimp.run_details", { defaultValue: "Run details" })}
                    </div>
                    {latestRunId && (
                      <div className="text-xs font-semibold text-slate-500 dark:text-slate-400">
                        #{latestRunId}
                      </div>
                    )}
                  </div>

                  {messageRunDetailLoading && (
                    <div className="mt-2 flex items-center gap-2 text-sm text-slate-700 dark:text-slate-200">
                      <span className="inline-flex h-4 w-4 animate-spin rounded-full border-2 border-slate-400/80 border-t-transparent dark:border-slate-500/70" />
                      {t("common.loading", { defaultValue: "Bezig..." })}
                    </div>
                  )}

                  {messageRunDetailError && (
                    <div className="mt-2 rounded-xl border border-red-200/70 bg-red-50/70 px-3 py-2 text-sm text-red-800 dark:border-red-500/40 dark:bg-red-900/30 dark:text-red-100">
                      {messageRunDetailError}
                    </div>
                  )}

                  {!messageRunDetailLoading && !messageRunDetailError && (
                    <div className="mt-2 space-y-2">
                      {(
                        [
                          {
                            key: "suppressedEmails",
                            label: "Suppressed",
                            value: messageRunDetail?.suppressedEmails,
                          },
                          { key: "failedEmails", label: "Failed", value: messageRunDetail?.failedEmails },
                          { key: "invalidEmails", label: "Invalid", value: messageRunDetail?.invalidEmails },
                        ] as const
                      ).map((section) => {
                        const entries = Object.entries(section.value || {});
                        const count = entries.length;
                        return (
                          <details
                            key={section.key}
                            className="rounded-xl border border-slate-200/70 bg-white/60 p-2 dark:border-slate-800/70 dark:bg-slate-950/10"
                            open={count > 0 && section.key === "suppressedEmails"}
                          >
                            <summary className="cursor-pointer select-none list-none text-sm font-semibold text-slate-800 dark:text-slate-100">
                              <span className="inline-flex items-center gap-2">
                                <span>{section.label}</span>
                                <span className="rounded-full bg-slate-900/5 px-2 py-0.5 text-xs font-bold text-slate-700 dark:bg-white/10 dark:text-slate-200">
                                  {count}
                                </span>
                              </span>
                            </summary>
                            {count === 0 ? (
                              <div className="mt-2 text-sm text-slate-500 dark:text-slate-400">—</div>
                            ) : (
                              <div className="mt-2 space-y-2">
                                {entries.slice(0, 200).map(([email, meta]) => {
                                  const title =
                                    (meta && (meta as any).title) ||
                                    (meta && (meta as any).message) ||
                                    (meta && (meta as any).detail) ||
                                    "";
                                  return (
                                    <div
                                      key={email}
                                      className="rounded-xl border border-slate-200/70 bg-white/80 px-3 py-2 dark:border-slate-800/70 dark:bg-slate-950/10"
                                    >
                                      <div className="text-sm font-semibold text-slate-900 dark:text-slate-100 break-words">
                                        {email}
                                      </div>
                                      {title ? (
                                        <div className="mt-0.5 text-xs text-slate-600 dark:text-slate-300 break-words">
                                          {String(title)}
                                        </div>
                                      ) : null}
                                    </div>
                                  );
                                })}
                                {count > 200 && (
                                  <div className="text-xs font-semibold text-slate-500 dark:text-slate-400">
                                    {t("common.more", { defaultValue: "Meer" })}: {count - 200}
                                  </div>
                                )}
                              </div>
                            )}
                          </details>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {status?.message && (
                <details className="rounded-2xl border border-slate-200/70 bg-white/70 p-3 dark:border-slate-800/70 dark:bg-slate-950/10">
                  <summary className="cursor-pointer text-xs font-semibold text-slate-600 dark:text-slate-300">
                    {t("common.raw", { defaultValue: "Ruwe data" })}
                  </summary>
                  <pre className="mt-2 overflow-auto whitespace-pre-wrap break-words text-xs text-slate-800 dark:text-slate-100">
                    {String(status.message)}
                  </pre>
                </details>
              )}
            </div>
          </aside>
        </div>
      )}
    </LdsOverviewPage>
  );
};

export default AdminMaintenanceMailchimp;
